import type { ReactChildrenType } from '@/types';

import {
  AuthProviderAws,
  forceRefreshTokensAws,
  getIdTokenAws,
  isAuthenticatedAws,
  signOutAws,
} from './aws';
import {
  AuthProviderAzure,
  forceRefreshTokensAzure,
  getIdTokenAzure,
  isAuthenticatedAzure,
  signOutAzure,
} from './azure';
import { postAuthMessage } from './util';

const cloudProvider = import.meta.env.VITE_CLOUD_PROVIDER;
const isAuthDisabled =
  import.meta.env.VITE_DISABLE_AUTH === 'true' || import.meta.env.VITEST;

const AuthProvider: React.FunctionComponent<ReactChildrenType> = ({
  children,
}) => {
  if (isAuthDisabled) {
    return <>{children}</>;
  }

  if (cloudProvider === 'aws') {
    return <AuthProviderAws>{children}</AuthProviderAws>;
  } else if (cloudProvider === 'azure') {
    return <AuthProviderAzure>{children}</AuthProviderAzure>;
  }
  throw new Error('Cloud provider not supported');
};

const getIdToken = async () => {
  if (isAuthDisabled) {
    return 'test_token';
  }

  if (cloudProvider === 'aws') {
    return await getIdTokenAws();
  } else if (cloudProvider === 'azure') {
    return await getIdTokenAzure();
  }
  throw new Error('Cloud provider not supported');
};

const isAuthenticated = async () => {
  if (isAuthDisabled) {
    return true;
  }

  let isAuthenticatedHandler: () => Promise<boolean>;
  if (cloudProvider === 'aws') {
    isAuthenticatedHandler = isAuthenticatedAws;
  } else if (cloudProvider === 'azure') {
    isAuthenticatedHandler = isAuthenticatedAzure;
  } else {
    throw new Error('Cloud provider not supported');
  }

  const isAuthenticated = await isAuthenticatedHandler();
  postAuthMessage(isAuthenticated);
  return isAuthenticated;
};

const signOut = async () => {
  if (isAuthDisabled) {
    return;
  }

  if (cloudProvider === 'aws') {
    await signOutAws();
  } else if (cloudProvider === 'azure') {
    await signOutAzure();
  } else {
    throw new Error('Cloud provider not supported');
  }

  window.localStorage.clear();
};

const forceRefreshTokens = async () => {
  if (isAuthDisabled) {
    return;
  }

  if (cloudProvider === 'aws') {
    return await forceRefreshTokensAws();
  } else if (cloudProvider === 'azure') {
    return await forceRefreshTokensAzure();
  }
  throw new Error('Cloud provider not supported');
};

export {
  AuthProvider,
  getIdToken,
  isAuthenticated,
  signOut,
  forceRefreshTokens,
};
